import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Shield, 
  CheckCircle, 
  XCircle,
  Clock,
  AlertTriangle,
  Calendar,
  MapPin,
  Loader2,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminDashboard = () => {
  const { isAdmin } = useAuth();
  const [reviewQueue, setReviewQueue] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [queueRes, reportsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/review-queue`),
        axios.get(`${API_URL}/admin/reports`)
      ]);
      setReviewQueue(queueRes.data.items);
      setReports(reportsRes.data.reports);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (queueId) => {
    setProcessing(queueId);
    try {
      await axios.post(`${API_URL}/admin/review-queue/${queueId}/approve`);
      toast.success('Item approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (queueId) => {
    setProcessing(queueId);
    try {
      await axios.post(`${API_URL}/admin/review-queue/${queueId}/reject`);
      toast.success('Item rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const handleCloseReport = async (reportId) => {
    try {
      await axios.post(`${API_URL}/admin/reports/${reportId}/close`);
      toast.success('Report closed');
      fetchData();
    } catch (error) {
      toast.error('Failed to close report');
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-16">
        <Shield className="w-16 h-16 text-cream/30 mx-auto mb-4" />
        <h2 className="text-xl font-display text-white mb-2">Admin Access Required</h2>
        <p className="text-cream/60">You don't have permission to view this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-sm bg-gold flex items-center justify-center">
            <Shield className="w-6 h-6 text-deep-green" />
          </div>
          <div>
            <h1 className="font-display font-bold text-2xl text-white">Admin Console</h1>
            <p className="text-cream/70 text-sm">Manage reviews, reports, and platform content</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchData}
          className="border-white/30 text-white hover:bg-white/10"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{reviewQueue.length}</p>
            <p className="text-cream/60 text-sm">Pending Reviews</p>
          </CardContent>
        </Card>
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-8 h-8 text-orange-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{reports.length}</p>
            <p className="text-cream/60 text-sm">Open Reports</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="reviews">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger 
            value="reviews"
            className="data-[state=active]:bg-gold data-[state=active]:text-deep-green"
          >
            Review Queue ({reviewQueue.length})
          </TabsTrigger>
          <TabsTrigger 
            value="reports"
            className="data-[state=active]:bg-gold data-[state=active]:text-deep-green"
          >
            Reports ({reports.length})
          </TabsTrigger>
        </TabsList>

        {/* Review Queue */}
        <TabsContent value="reviews" className="mt-6">
          {reviewQueue.length === 0 ? (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <p className="text-cream/60">All caught up! No items pending review.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {reviewQueue.map((item) => (
                <Card key={item.queue_id} className="bg-white/5 border-white/10">
                  <CardContent className="p-5">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge className="bg-blue-500/20 text-blue-400">
                            {item.item_type}
                          </Badge>
                          <Badge variant="outline" className="border-white/20 text-cream/70">
                            {item.reason}
                          </Badge>
                        </div>
                        
                        {item.item_data && (
                          <div className="mb-3">
                            <h3 className="font-semibold text-white text-lg">
                              {item.item_data.title || item.item_data.name}
                            </h3>
                            {(item.item_data.city || item.item_data.state) && (
                              <p className="text-cream/60 flex items-center gap-1 text-sm">
                                <MapPin className="w-4 h-4" />
                                {item.item_data.city}, {item.item_data.state}
                              </p>
                            )}
                            {item.item_data.start_at && (
                              <p className="text-cream/60 flex items-center gap-1 text-sm">
                                <Calendar className="w-4 h-4" />
                                {format(new Date(item.item_data.start_at), 'MMM d, yyyy')}
                              </p>
                            )}
                          </div>
                        )}
                        
                        <p className="text-cream/50 text-xs">
                          Submitted: {format(new Date(item.created_at), 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApprove(item.queue_id)}
                          className="bg-green-600 hover:bg-green-500"
                          disabled={processing === item.queue_id}
                          data-testid={`approve-${item.queue_id}`}
                        >
                          {processing === item.queue_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Approve
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleReject(item.queue_id)}
                          variant="destructive"
                          disabled={processing === item.queue_id}
                          data-testid={`reject-${item.queue_id}`}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Reports */}
        <TabsContent value="reports" className="mt-6">
          {reports.length === 0 ? (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <p className="text-cream/60">No open reports.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {reports.map((report) => (
                <Card key={report.report_id} className="bg-white/5 border-white/10">
                  <CardContent className="p-5">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-orange-500/20 text-orange-400">
                            {report.target_type}
                          </Badge>
                        </div>
                        <p className="text-white mb-1">
                          <span className="text-cream/60">Reason:</span> {report.reason}
                        </p>
                        <p className="text-cream/50 text-xs">
                          Reported: {format(new Date(report.created_at), 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>
                      <Button
                        onClick={() => handleCloseReport(report.report_id)}
                        variant="outline"
                        className="border-white/30 text-white hover:bg-white/10"
                      >
                        Close Report
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
